'use client'

import { memo, useMemo } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import dynamic from 'next/dynamic'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Montserrat_Underline } from '@/app/fonts/google-font-fallbacks'
import { CMS_DEMO_ASSET_URLS } from '@/lib/cms/demo-assets'
import {
  ArrowRight,
  Plus,
  Zap,
  Layout,
  ShieldCheck,
  Landmark,
  LineChart,
  ReceiptIndianRupee,
  Search,
  Command,
  Globe,
  CreditCard,
  Building2,
  Wallet,
  MessageSquare,
  Bot,
  Check,
  Bitcoin,
  MessageCircle,
  Send,
  Workflow,
  Sparkles,
  Mail,
  Calendar,
  Twitter,
  Heart,
  FileText,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  RotateCcw,
  RotateCw,
  Pause,
  Play,
  Inbox,
  Apple,
  Smartphone
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useState, useRef, useCallback } from 'react'
import { useCurrency } from './hooks/useCurrency'
import { NingenieConnectPlatformSection } from './_components/ningenie-connect-platform-section'

// baby: Montserrat Underline font for NINGenie branding (same as main page)
const montserratSubrayada = Montserrat_Underline({
  weight: ['400', '700'],
  subsets: ['latin'],
  display: 'swap',
  // baby: No capsize metrics for "Montserrat Underline" key in Next.js (legacy: Subrayada); skip fallback CSS tuning
  adjustFontFallback: false,
})

// baby: Grid background component matching reference (large faint squares)
const GridPattern = () => (
  <div className="absolute inset-0 -z-10 h-full w-full bg-white pointer-events-none">
    <div className="absolute h-full w-full bg-[linear-gradient(to_right,#f1f5f9_1px,transparent_1px),linear-gradient(to_bottom,#f1f5f9_1px,transparent_1px)] bg-[size:6rem_6rem]" />
    {/* Hero glow - exact match from old page */}
    <div className="absolute right-1/2 h-80 w-[120%] translate-x-1/2 rounded-[60%] bg-[#21728d]/20 blur-3xl" />
  </div>
)

// baby: Circuit SVG to match the intricate lines in reference
const CircuitBackground = () => (
  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1200px] h-[800px] -z-1 pointer-events-none opacity-30">
    <svg width="100%" height="100%" viewBox="0 0 1200 800" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="trace_grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#cbd5e1" stopOpacity="0.2" />
          <stop offset="50%" stopColor="#94a3b8" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#cbd5e1" stopOpacity="0.2" />
        </linearGradient>
      </defs>

      {/* Top Left Traces */}
      <path d="M 450 300 L 400 250 L 200 250" stroke="url(#trace_grad)" strokeWidth="1.5" />
      <circle cx="200" cy="250" r="3" fill="#cbd5e1" />
      <path d="M 480 300 L 440 220 L 300 220" stroke="url(#trace_grad)" strokeWidth="1.5" />
      <circle cx="300" cy="220" r="3" fill="#cbd5e1" />
      <path d="M 500 280 L 500 150 L 400 150" stroke="url(#trace_grad)" strokeWidth="1.5" />
      <circle cx="400" cy="150" r="3" fill="#cbd5e1" />

      {/* Top Right Traces */}
      <path d="M 750 300 L 800 250 L 1000 250" stroke="url(#trace_grad)" strokeWidth="1.5" />
      <circle cx="1000" cy="250" r="3" fill="#cbd5e1" />
      <path d="M 720 300 L 760 220 L 900 220" stroke="url(#trace_grad)" strokeWidth="1.5" />
      <circle cx="900" cy="220" r="3" fill="#cbd5e1" />
      <path d="M 700 280 L 700 150 L 800 150" stroke="url(#trace_grad)" strokeWidth="1.5" />
      <circle cx="800" cy="150" r="3" fill="#cbd5e1" />

      {/* Bottom Left Traces */}
      <path d="M 450 500 L 400 550 L 200 550" stroke="url(#trace_grad)" strokeWidth="1.5" />
      <circle cx="200" cy="550" r="3" fill="#cbd5e1" />
      <path d="M 480 500 L 440 580 L 300 580" stroke="url(#trace_grad)" strokeWidth="1.5" />
      <circle cx="300" cy="580" r="3" fill="#cbd5e1" />
      <path d="M 500 520 L 500 650 L 400 650" stroke="url(#trace_grad)" strokeWidth="1.5" />
      <circle cx="400" cy="650" r="3" fill="#cbd5e1" />

      {/* Bottom Right Traces */}
      <path d="M 750 500 L 800 550 L 1000 550" stroke="url(#trace_grad)" strokeWidth="1.5" />
      <circle cx="1000" cy="550" r="3" fill="#cbd5e1" />
      <path d="M 720 500 L 760 580 L 900 580" stroke="url(#trace_grad)" strokeWidth="1.5" />
      <circle cx="900" cy="580" r="3" fill="#cbd5e1" />
      <path d="M 700 520 L 700 650 L 800 650" stroke="url(#trace_grad)" strokeWidth="1.5" />
      <circle cx="800" cy="650" r="3" fill="#cbd5e1" />
    </svg>
  </div>
)

// baby: demo data for the plugin showcase carousel - using mp4 videos
const PLUGIN_DEMOS = [
  {
    step: 'Step 1',
    title: 'How to manage Gmail?',
    description: 'Search, organize, and respond to emails with simple voice commands. Genie reads your inbox, filters important messages, and drafts replies.',
    icon: Mail,
    color: '#EA4335',
    demoSrc: CMS_DEMO_ASSET_URLS.gmail,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster
  },
  {
    step: 'Step 2',
    title: 'How to use Calendar?',
    description: 'Check your schedule, create events, and get reminders. Just say "What\'s on my calendar today?" and Genie shows your day at a glance.',
    icon: Calendar,
    color: '#4285F4',
    demoSrc: CMS_DEMO_ASSET_URLS.calendar,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster
  },
  {
    step: 'Step 3',
    title: 'How to post on Twitter/X?',
    description: 'Draft tweets, schedule posts, and manage your social presence. Genie helps you craft engaging content and post at optimal times.',
    icon: Twitter,
    color: '#1DA1F2',
    demoSrc: CMS_DEMO_ASSET_URLS.twitterX,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster
  },
  {
    step: 'Step 4',
    title: 'How to check Health?',
    description: 'Track your symptoms, set health reminders, and get personalized insights. Genie integrates with your health apps to keep you informed.',
    icon: Heart,
    color: '#FF6B6B',
    demoSrc: CMS_DEMO_ASSET_URLS.health,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster
  },
  {
    step: 'Step 5',
    title: 'How to manage Notion?',
    description: 'Search, summarize, and organize your documents. Genie can find that important file and extract key information for you.',
    icon: FileText,
    color: '#7C3AED',
    demoSrc: CMS_DEMO_ASSET_URLS.gmail,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster
  },
  {
    step: 'Step 6',
    title: 'Auto cancel open orders on Groww/Zerodha?',
    description: 'Cancel positions, check watchlists, and get market insights from your Groww account. Genie brings your portfolio data right to the chat.',
    icon: TrendingUp,
    color: '#387ED1',
    demoSrc: CMS_DEMO_ASSET_URLS.groww,
    posterSrc: CMS_DEMO_ASSET_URLS.healthPoster
  },
  // {
  //   step: 'Step 7',
  //   title: 'How to use Binance?',
  //   description: 'Check your crypto balances, view market data, and monitor your Binance portfolio. Genie shows your holdings in real-time.',
  //   icon: Wallet,
  //   color: '#F0B90B',
  //   demoSrc: CMS_DEMO_ASSET_URLS.groww,
  //   posterSrc: CMS_DEMO_ASSET_URLS.healthPoster
  // },
  // {
  //   step: 'Step 8',
  //   title: 'How to use Contacts?',
  //   description: 'Search your contacts, share contact info, and manage your address book with simple commands. Genie finds who you need instantly.',
  //   icon: MessageSquare,
  //   color: '#25D366',
  //   demoSrc: CMS_DEMO_ASSET_URLS.gmail,
  //   posterSrc: CMS_DEMO_ASSET_URLS.healthPoster
  // }
]

// baby: Plugin Demo Carousel Component - adapted for V2 aesthetic
const PluginDemoCarousel = memo(function PluginDemoCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loadedVideos, setLoadedVideos] = useState<Set<number>>(new Set())
  const [failedVideos, setFailedVideos] = useState<Set<number>>(new Set())
  const [isPlaying, setIsPlaying] = useState(true)
  const [isHovering, setIsHovering] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? PLUGIN_DEMOS.length - 1 : prev - 1))
    setIsPlaying(true)
  }, [])

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === PLUGIN_DEMOS.length - 1 ? 0 : prev + 1))
    setIsPlaying(true)
  }, [])

  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index)
    setIsPlaying(true)
  }, [])

  const handleVideoCanPlay = useCallback((index: number) => {
    setLoadedVideos((prev) => new Set(prev).add(index))
    setFailedVideos((prev) => {
      const next = new Set(prev)
      next.delete(index)
      return next
    })
  }, [])

  const handleVideoError = useCallback((index: number) => {
    setFailedVideos((prev) => new Set(prev).add(index))
  }, [])

  const togglePlayPause = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause()
      } else {
        videoRef.current.play()
      }
      setIsPlaying(!isPlaying)
    }
  }, [isPlaying])

  const seekBackward = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 5)
    }
  }, [])

  const seekForward = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.min(
        videoRef.current.duration,
        videoRef.current.currentTime + 5
      )
    }
  }, [])

  const isCurrentVideoLoaded = loadedVideos.has(currentIndex)
  const isCurrentVideoFailed = failedVideos.has(currentIndex)

  const currentDemo = PLUGIN_DEMOS[currentIndex]
  const IconComponent = currentDemo.icon

  return (
    <div className="grid gap-8 md:gap-12 lg:grid-cols-[1fr_1.2fr] lg:items-center">
      <div className="flex flex-col gap-6 order-2 lg:order-1">
        <div className="flex items-center gap-3">
          <div
            className="flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-100 shadow-sm"
            style={{ backgroundColor: `${currentDemo.color}15` }}
          >
            <IconComponent className="h-6 w-6" style={{ color: currentDemo.color }} />
          </div>
          <span className="text-sm font-bold uppercase tracking-wider text-slate-500 bg-slate-50 px-2 py-1 rounded-md border border-slate-100">
            {currentDemo.step}
          </span>
        </div>

        <h3 className="text-3xl font-bold tracking-tight text-slate-900 md:text-4xl leading-tight">
          {currentDemo.title}
        </h3>

        <p className="text-base text-slate-600 md:text-lg leading-relaxed">
          {currentDemo.description}
        </p>

        <div className="flex items-center gap-2 mt-2">
          {PLUGIN_DEMOS.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-1.5 rounded-full transition-all duration-300 ${currentIndex === index
                ? 'bg-[#21728d] w-6'
                : 'bg-slate-200 hover:bg-slate-300 w-2'
                }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        <div className="flex items-center gap-3 mt-2">
          <Button
            variant="outline"
            size="icon"
            onClick={goToPrevious}
            className="h-12 w-12 rounded-xl border-slate-200 hover:bg-slate-50 hover:border-[#21728d] bg-[#88a1ac]/10"
            aria-label="Previous demo"
          >
            <ChevronLeft className="h-5 w-5 text-[#21728d]" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={goToNext}
            className="h-12 w-12 rounded-xl border-slate-200 hover:bg-slate-50 hover:border-[#21728d] bg-[#88a1ac]/10"
            aria-label="Next demo"
          >
            <ChevronRight className="h-5 w-5 text-[#21728d]" />
          </Button>
          <span className="text-sm font-medium text-slate-500 ml-2">
            step {currentIndex + 1} of {PLUGIN_DEMOS.length}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-center order-1 lg:order-2">
        <div className="relative w-full max-w-[320px]">
          <div className="relative rounded-[3rem] border-[4px] border-slate-900/5 bg-slate-50 p-2 shadow-2xl">
            <div className="relative rounded-[2.5rem] border-[4px] border-slate-900 bg-slate-900 overflow-hidden">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-slate-900 rounded-b-2xl z-20" />

              <div
                className="relative aspect-[9/19.5] bg-slate-50 overflow-hidden rounded-[2.1rem]"
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
              >
                {!isCurrentVideoFailed && (
                  <video
                    ref={videoRef}
                    key={currentDemo.demoSrc}
                    className="absolute inset-0 w-full h-full object-cover z-10"
                    src={currentDemo.demoSrc}
                    poster={currentDemo.posterSrc}
                    autoPlay
                    muted
                    loop
                    playsInline
                    onCanPlay={() => handleVideoCanPlay(currentIndex)}
                    onError={() => handleVideoError(currentIndex)}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                  />
                )}

                {isCurrentVideoLoaded && !isCurrentVideoFailed && (
                  <div
                    className={`absolute inset-0 z-30 flex items-center justify-center bg-black/40 transition-opacity duration-300 ${isHovering ? 'opacity-100' : 'opacity-0 pointer-events-none'
                      }`}
                  >
                    <div className="flex items-center gap-4">
                      <button
                        onClick={seekBackward}
                        className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-all hover:bg-white/30 hover:scale-110 border border-white/10"
                        aria-label="Rewind 5 seconds"
                      >
                        <RotateCcw className="h-5 w-5" />
                      </button>

                      <button
                        onClick={togglePlayPause}
                        className="flex h-14 w-14 items-center justify-center rounded-full bg-white/30 text-white backdrop-blur-sm transition-all hover:bg-white/40 hover:scale-110 border border-white/10"
                        aria-label={isPlaying ? 'Pause' : 'Play'}
                      >
                        {isPlaying ? (
                          <Pause className="h-7 w-7" />
                        ) : (
                          <Play className="h-7 w-7 ml-1" />
                        )}
                      </button>

                      <button
                        onClick={seekForward}
                        className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-all hover:bg-white/30 hover:scale-110 border border-white/10"
                        aria-label="Forward 5 seconds"
                      >
                        <RotateCw className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                )}

                {(!isCurrentVideoLoaded || isCurrentVideoFailed) && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 z-0">
                    <div
                      className="flex h-20 w-20 items-center justify-center rounded-2xl mb-4 border border-slate-200"
                      style={{ backgroundColor: `${currentDemo.color}15` }}
                    >
                      <IconComponent className="h-10 w-10" style={{ color: currentDemo.color }} />
                    </div>
                    <span className="text-sm font-medium text-slate-500">
                      {isCurrentVideoFailed ? 'Video unavailable' : 'Loading...'}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="absolute inset-0 z-[50] rounded-[3rem] shadow-[inset_0_0_20px_rgba(0,0,0,0.1)] pointer-events-none" />
          </div>

          <div
            className="absolute -inset-10 -z-10 rounded-full blur-3xl opacity-20"
            style={{ backgroundColor: currentDemo.color }}
          />
        </div>
      </div>
    </div>
  )
})

// baby: dynamically import Lottie to avoid SSR issues
const Lottie = dynamic(() => import('lottie-react'), { ssr: false })

// baby: fetch animation data function for React Query
const fetchAnimationData = async (): Promise<Record<string, unknown>> => {
  const response = await fetch('/jsons/ninx_ai_chat_lottie.json')
  if (!response.ok) {
    throw new Error(`Failed to load chat AI animation: ${response.status}`)
  }
  return response.json()
}

const FAQ_ITEMS_BASE = [
  {
    question: "Why is our tool different than the rest AI tools?",
    answer: "NIN Genie is an agentic AI that doesn't just answer questions...it executes tasks across your apps. From managing emails to trading stocks, it turns intent into action securely and efficiently, acting as a true digital extension of yourself."
  },
  {
    question: "How does our pricing work?",
    // answer is dynamic: replaced at render time with the detected currency
    answer: ""
  },
  {
    question: "What is our implementation process?",
    answer: "Getting started is instant. Download the app, connect your accounts (Gmail, Calendar, Brokers, etc.) via our secure integrations, and start tasking immediately. No complex setup or coding is required to build your own automations."
  }
]

const LandingPageContentComponent = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null)
  const { currency, isLoading: isCurrencyLoading, formatPrice } = useCurrency()
  const goToPlansPage = useCallback(() => {
    window.location.href = '/ningenie/plans'
  }, [])

  // baby: For INR we want to show the exact plan prices from the latest plan JSON,
  // baby: not a USD conversion approximation.
  const formatTierPrice = useCallback((priceUsd: number, priceInr?: number) => {
    if (currency.code === 'INR' && typeof priceInr === 'number') {
      return `₹${priceInr.toLocaleString('en-IN')}`
    }
    return formatPrice(priceUsd)
  }, [currency.code, formatPrice])

  // baby: Home pricing mirrors the latest plan JSON shared by the team.
  // baby: We intentionally show only Free, 2026 Pro, and 2026 Pro+ here.
  const PRICING_TIERS = useMemo(() => [
    {
      name: "Free",
      priceUsd: 0,
      priceInr: 0,
      desc: "A simple way to explore the platform.",
      credits: "20 credits",
      cta: "Get Started",
      details: ["Basic Plugins", "Up to 10 Automations", "Community support"],
    },
    {
      name: "Pro",
      priceUsd: 4.85,
      priceInr: 399,
      desc: "Smart automation for solo users.",
      credits: "420 credits",
      popular: true,
      cta: "Get Pro",
      details: ["Basic Plugins", "Up to 400 Automations", "Community support"],
    },
    {
      name: "Pro+",
      priceUsd: 18.99,
      priceInr: 1899,
      desc: "Premium workflows with full plugin access.",
      credits: "2,000 credits",
      cta: "Get Pro+",
      details: ["Full Plugins", "NIN Crew", "Up to 2,000 Automations", "Dedicated support"],
    },
  ], [])

  // baby: FAQ items with dynamic pricing in the answer text
  const FAQ_ITEMS = useMemo(() => {
    const proPrice = formatTierPrice(4.85, 399)
    const proPlusPrice = formatTierPrice(18.99, 1899)
    return FAQ_ITEMS_BASE.map((item) => {
      if (item.question === "How does our pricing work?") {
        return {
          ...item,
          answer: `We offer a flexible tier-based model starting with a free plan for exploration. Pro gives solo users 420 credits and core automation features (${proPrice}/mo), while Pro+ unlocks 2,000 credits, full plugins, and premium support (${proPlusPrice}/mo).`
        }
      }
      return item
    })
  }, [formatTierPrice])

  // baby: use React Query to load animation data
  const { data: chatAnimationData } = useQuery({
    queryKey: ['ningenie', 'home', 'chat-animation'],
    queryFn: fetchAnimationData,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 1000 * 60 * 60
  })

  return (
    <div className="min-h-screen bg-[#F6F9FD] text-slate-900 relative overflow-x-hidden font-sans selection:bg-[#21728d]/10">
      <GridPattern />

      {/* Navigation */}
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-6 md:px-8 relative z-50">
        <div className="flex items-center gap-2">
          <div className="h-12 w-12 flex items-center justify-center">
            {chatAnimationData && <Lottie animationData={chatAnimationData} loop={true} autoplay={true} style={{ width: '100%', height: '100%' }} />}
          </div>
          <span className={cn("text-xm font-semibold uppercase tracking-[0.2em] mr-8", montserratSubrayada.className)}>NINGENIE</span>
        </div>
        <div className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600 mr-16">
          <Link href="#community" className="hover:text-black transition-colors">Community</Link>
          <Link href="#enterprise" className="hover:text-black transition-colors">Enterprise</Link>
          <Link href="#resources" className="hover:text-black transition-colors">Resources</Link>
          <Link href="#careers" className="hover:text-black transition-colors">Careers</Link>
          <Link href="#connect-platform" className="hover:text-black transition-colors">Connect</Link>
          <a href="#pricing" onClick={(e) => { e.preventDefault(); document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }); }} className="hover:text-black transition-colors cursor-pointer">Pricing</a>
        </div>
        <div className="flex items-center gap-4">
          {/* <Button variant="ghost" className="text-sm font-medium hidden sm:flex">Sign in</Button> */}
          <Button className="rounded-md bg-[#21728d] text-white hover:bg-slate-800 px-6 h-10" onClick={() => document.getElementById('download')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>
            Download
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <main className="mx-auto max-w-7xl px-6 pt-16 pb-32 text-center md:px-8 relative z-10">

        <div className="flex flex-wrap justify-center gap-3 mb-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 shadow-sm">
            <span className="flex h-2 w-2 rounded-full bg-[#21728d]"></span>
            Introducing Agentic AI
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 shadow-sm">
            <ShieldCheck className="h-3 w-3 text-[#21728d]" />
            CASA + Certified
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 shadow-sm">
            <ShieldCheck className="h-3 w-3 text-[#21728d]" />
            SOC 2 in progress
          </div>
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 shadow-sm">
            <ShieldCheck className="h-3 w-3 text-[#21728d]" />
            ZDR on cloud credits
          </div>
        </div>

        <h1 className="mx-auto max-w-4xl text-5xl font-bold tracking-tight text-slate-900 md:text-7xl mb-6">
          Don't Ask, Just Task.
        </h1>
        <p className="mx-auto max-w-2xl text-lg text-slate-500 mb-12">
          Orchestrate your digital life by chatting with AI. From daily tasks to investments, Genie handles the plan, execution, and verification.
        </p>

        {/* Central Visual Stack */}
        <div className="relative w-full max-w-4xl mx-auto h-[800px]">

          {/* 1. Input Card */}
          <div className="relative z-30 mx-auto max-w-xl">
            <div className="relative rounded-[2rem] border border-slate-200 bg-white p-2 shadow-2xl shadow-slate-200/50">
              <div className="flex flex-col gap-3 p-4">
                <div className="relative">
                  <textarea
                    className="rounded-xl w-full resize-none border-0 bg-transparent p-2 text-lg font-medium placeholder:text-slate-300 focus:ring-0 min-h-[60px] focus:outline-none text-center"
                    placeholder="Type your intent..."
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full text-slate-400">
                    <Plus className="h-5 w-5" />
                  </Button>
                  <Button className="rounded-full bg-[#21728d] text-white hover:bg-slate-800 px-6 h-9" onClick={() => window.location.href = '/ningenie'}>
                    Execute now <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
              {/* Glow */}
              <div className="absolute -inset-px -z-10 rounded-[2rem] bg-gradient-to-r from-rose-100/50 via-sky-100/50 to-indigo-100/50 opacity-50 blur-lg" />
            </div>
          </div>

          {/* Connecting Lines Vertical */}
          <div className="absolute top-[180px] left-1/2 -translate-x-1/2 h-12 w-px bg-slate-200 z-20" />

          {/* 2. "Or Connect With" Card */}
          <div className="absolute top-[220px] left-1/2 -translate-x-1/2 z-30">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-4 flex flex-col items-center gap-3 min-w-[300px]">
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Or connect with</span>
              <div className="flex gap-3">
                <Button variant="outline" className="rounded-xl bg-white border-slate-200 h-9 gap-2 px-3 hover:bg-slate-50 hover:text-black text-xs">
                  <Landmark className="h-3.5 w-3.5" /> Banks
                </Button>
                <Button variant="outline" className="rounded-xl bg-white border-slate-200 h-9 gap-2 px-3 hover:bg-slate-50 hover:text-black text-xs">
                  <LineChart className="h-3.5 w-3.5" /> Brokers
                </Button>
                <Button variant="outline" className="rounded-xl bg-white border-slate-200 h-9 gap-2 px-3 hover:bg-slate-50 hover:text-black text-xs">
                  <Inbox className="h-3.5 w-3.5" /> Social Media
                </Button>
              </div>
            </div>
          </div>

          {/* Connecting Lines Vertical */}
          <div className="absolute top-[300px] left-1/2 -translate-x-1/2 h-20 w-px bg-slate-200 z-20" />

          {/* 3. Central Processor Chip */}
          <div className="absolute top-[380px] left-1/2 -translate-x-1/2 z-20">
            <div className="relative h-64 w-64 bg-white rounded-[3rem] border border-slate-200 shadow-xl flex items-center justify-center">
              <div className="absolute inset-0 rounded-[3rem] bg-gradient-to-b from-white to-slate-50/50" />

              {/* Decorative Dots - Corners */}
              <div className="absolute top-6 left-6 flex gap-1.5">
                <div className="h-2 w-2 rounded-full bg-slate-200" />
                <div className="h-2 w-2 rounded-full bg-slate-200" />
                <div className="h-2 w-2 rounded-full bg-slate-200" />
              </div>
              <div className="absolute top-6 right-6 flex gap-1.5">
                <div className="h-2 w-2 rounded-full bg-slate-200" />
                <div className="h-2 w-2 rounded-full bg-slate-200" />
                <div className="h-2 w-2 rounded-full bg-slate-200" />
              </div>
              <div className="absolute bottom-6 left-6 flex gap-1.5">
                <div className="h-2 w-2 rounded-full bg-slate-200" />
                <div className="h-2 w-2 rounded-full bg-slate-200" />
                <div className="h-2 w-2 rounded-full bg-slate-200" />
              </div>
              <div className="absolute bottom-6 right-6 flex gap-1.5">
                <div className="h-2 w-2 rounded-full bg-slate-200" />
                <div className="h-2 w-2 rounded-full bg-slate-200" />
                <div className="h-2 w-2 rounded-full bg-slate-200" />
              </div>

              {/* Pins - Sides */}
              <div className="absolute top-1/2 -translate-y-1/2 -left-2 flex flex-col gap-3">
                {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-1.5 bg-slate-300 rounded-full" />)}
              </div>
              <div className="absolute top-1/2 -translate-y-1/2 -right-2 flex flex-col gap-3">
                {[1, 2, 3, 4].map(i => <div key={i} className="h-4 w-1.5 bg-slate-300 rounded-full" />)}
              </div>
              <div className="absolute left-1/2 -translate-x-1/2 -top-2 flex gap-3">
                {[1, 2, 3, 4].map(i => <div key={i} className="w-4 h-1.5 bg-slate-300 rounded-full" />)}
              </div>
              <div className="absolute left-1/2 -translate-x-1/2 -bottom-2 flex gap-3">
                {[1, 2, 3, 4].map(i => <div key={i} className="w-4 h-1.5 bg-slate-300 rounded-full" />)}
              </div>

              {/* Inner Core (Lottie) */}
              <div className="relative w-32 h-32 bg-white rounded-[2rem] shadow-inner border border-slate-100 flex items-center justify-center overflow-hidden">
                {chatAnimationData ? (
                  <div className="w-[140%] h-[140%] flex items-center justify-center">
                    <Lottie animationData={chatAnimationData} loop={true} autoplay={true} style={{ width: '100%', height: '100%' }} />
                  </div>
                ) : (
                  <div className="h-16 w-16 rounded-full bg-gradient-to-tr from-[#21728d] to-[#88a1ac] shadow-lg animate-pulse" />
                )}
              </div>
            </div>
          </div>

          {/* Circuit SVG Background placed behind chip */}
          <div className="absolute top-[380px] left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full z-10">
            <CircuitBackground />
          </div>

          {/* Flow Explanations (Floating Chips - Scattered) */}

          {/* Left Side - User Intents / Queries */}
          <div className="absolute top-[340px] left-10 z-30 hidden md:flex items-center gap-2 bg-white p-2 rounded-lg border border-slate-100 shadow-sm opacity-80 hover:opacity-100 transition-opacity">
            <MessageSquare className="h-4 w-4 text-slate-400" />
            <span className="text-xs font-medium text-slate-500">"Organize my emails"</span>
          </div>
          <div className="absolute top-[440px] -left-8 z-30 hidden md:flex items-center gap-2 bg-white p-2 rounded-lg border border-slate-100 shadow-sm opacity-80 hover:opacity-100 transition-opacity">
            <Sparkles className="h-4 w-4 text-slate-400" />
            <span className="text-xs font-medium text-slate-500">"Split dinner bill"</span>
          </div>
          <div className="absolute top-[540px] left-4 z-30 hidden md:flex items-center gap-2 bg-white p-2 rounded-lg border border-slate-100 shadow-sm opacity-80 hover:opacity-100 transition-opacity">
            <Search className="h-4 w-4 text-slate-400" />
            <span className="text-xs font-medium text-slate-500">"Find my tax docs"</span>
          </div>

          {/* Right Side - Tool Calls / Agent Steps */}
          <div className="absolute top-[350px] right-10 z-30 hidden md:flex items-center gap-2 bg-white p-2 rounded-lg border border-slate-100 shadow-sm opacity-80 hover:opacity-100 transition-opacity">
            <Bot className="h-4 w-4 text-slate-400" />
            <span className="text-xs font-medium text-slate-500">Calling Gmail API...</span>
          </div>
          <div className="absolute top-[450px] -right-8 z-30 hidden md:flex items-center gap-2 bg-white p-2 rounded-lg border border-slate-100 shadow-sm opacity-80 hover:opacity-100 transition-opacity">
            <Workflow className="h-4 w-4 text-slate-400" />
            <span className="text-xs font-medium text-slate-500">Requesting Payment</span>
          </div>
          <div className="absolute top-[550px] right-4 z-30 hidden md:flex items-center gap-2 bg-white p-2 rounded-lg border border-slate-100 shadow-sm opacity-80 hover:opacity-100 transition-opacity">
            <ShieldCheck className="h-4 w-4 text-slate-400" />
            <span className="text-xs font-medium text-slate-500">Verifying Amount</span>
          </div>

        </div>

        {/* Download Buttons - Centered */}
        <div id="download" className="flex flex-col gap-8 justify-center items-center mt-1 mb-60 z-30 relative">
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link
              href="https://apps.apple.com/app/ningenie-dont-ask-just-task/id6755959217"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-black px-5 py-3 text-white shadow-lg shadow-slate-200/50 transition hover:bg-slate-800 hover:-translate-y-0.5"
            >
              <Apple className="h-6 w-6" />
              <div className="flex flex-col items-start">
                <span className="text-[10px] leading-none text-slate-300">Download on the</span>
                <span className="text-sm font-semibold leading-tight">App Store</span>
              </div>
            </Link>
            <div className="inline-flex items-center gap-2 rounded-xl bg-white border border-slate-200 px-5 py-3 text-slate-400 cursor-not-allowed shadow-sm">
              <Smartphone className="h-6 w-6" />
              <div className="flex flex-col items-start">
                <span className="text-[10px] leading-none">Coming Soon</span>
                <span className="text-sm font-semibold leading-tight">Android</span>
              </div>
            </div>
          </div>

          {/* Other products - NINX desktop, less focus */}
          <div className="flex flex-col items-center gap-2">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Other products</span>
            <Link
              href="/ninxl"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white/80 px-4 py-2.5 text-slate-600 shadow-sm transition hover:bg-slate-50 hover:border-slate-300 hover:text-slate-800"
            >
              <Image
                src="/ninx_logo_light.svg"
                alt="NINX"
                width={24}
                height={24}
                className="h-6 w-6 shrink-0"
              />
              <span className={cn("text-xs font-semibold tracking-[0.2em]", montserratSubrayada.className)}>NINX</span>
            </Link>
          </div>
        </div>

        {/* Trusted By Section - with Crop Marks */}
        <div className="relative mt-12 max-w-5xl mx-auto pt-16 pb-16">
          {/* Corner Markers */}
          <div className="absolute top-0 left-0 w-4 h-4 border-l border-t border-slate-300" />
          <div className="absolute top-0 right-0 w-4 h-4 border-r border-t border-slate-300" />
          <div className="absolute bottom-0 left-0 w-4 h-4 border-l border-b border-slate-300" />
          <div className="absolute bottom-0 right-0 w-4 h-4 border-r border-b border-slate-300" />

          <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-12 text-center">Trusted by 200+ partners</p>
          <div className="flex flex-wrap justify-center gap-12 md:gap-24 grayscale opacity-60">
            <div className="flex items-center gap-2 font-bold text-xl text-slate-800"><Building2 className="h-6 w-6" /> Axis</div>
            <div className="flex items-center gap-2 font-bold text-xl text-slate-800"><Wallet className="h-6 w-6" /> Binance</div>
            <div className="flex items-center gap-2 font-bold text-xl text-slate-800"><LineChart className="h-6 w-6" /> Zerodha</div>
            <div className="flex items-center gap-2 font-bold text-xl text-slate-800"><CreditCard className="h-6 w-6" /> NPCI</div>
          </div>
        </div>

      </main>

      {/* baby: Plugin Demo Videos Section - V2 styled */}
      <section className="py-24 bg-slate-50/50 border-t border-slate-100 relative overflow-hidden">
        <GridPattern />
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-16 px-6 md:px-10 lg:px-16 relative z-10">
          <div className="flex flex-col gap-4">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 w-fit shadow-sm">
              <span className="flex h-2 w-2 rounded-full bg-[#21728d]"></span>
              Live Demonstrations
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-slate-900 md:text-5xl">See Genie in action</h2>
            <p className="max-w-3xl text-lg text-slate-500">
              Watch how NIN Genie connects to your favorite apps and services. A seamless layer of intelligence over your existing tools.
            </p>
          </div>

          <PluginDemoCarousel />
        </div>
      </section>

      <NingenieConnectPlatformSection />

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-slate-50/50 border-t border-slate-100">
        <div className="mx-auto max-w-6xl px-6 md:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 mb-6 shadow-sm">
              <span className="flex h-2 w-2 rounded-full bg-[#ff5500]"></span>
              Pricing
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Flexible Pricing</h2>
            <p className="text-lg text-slate-500 max-w-2xl mx-auto">Explore simple, transparent pricing built for real-world AI workloads.</p>
            {!isCurrencyLoading && currency.code !== 'USD' && (
              <div className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-500 mt-3 shadow-sm">
                <Globe className="h-3 w-3 text-[#21728d]" />
                Prices shown in {currency.code}
              </div>
            )}

            <div className="flex items-center justify-center gap-3 mt-8">
              <span className="text-sm font-medium text-slate-600">Monthly</span>
              <Switch />
              <span className="text-sm font-medium text-slate-900">Yearly <span className="text-[#21728d] text-xs ml-1 font-normal">(-20%)</span></span>
            </div>
          </div>

          <div className="grid gap-6 items-stretch md:grid-cols-3">
            {PRICING_TIERS.map((tier, i) => {
              const displayPrice = formatTierPrice(tier.priceUsd, tier.priceInr)
              return (
                <Card key={i} className={cn("relative flex h-full flex-col border-slate-200 bg-white transition-all hover:-translate-y-1 hover:shadow-lg", tier.popular && "border-[#21728d] shadow-md")}>
                  {tier.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#21728d] text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">Most Popular</div>
                  )}
                  <CardHeader className="pb-4">
                    <h3 className="font-semibold text-slate-900">{tier.name}</h3>
                    <p className="text-xs text-slate-500 mt-1 min-h-[40px]">{tier.desc}</p>
                  </CardHeader>
                  <CardContent className="flex flex-1 flex-col">
                    <div className="flex items-baseline gap-1 mb-6">
                      <span className={cn("font-bold text-slate-900", isCurrencyLoading ? "text-3xl opacity-0 animate-pulse" : "text-3xl")}>
                        {displayPrice}
                      </span>
                      {tier.priceUsd !== 0 && <span className="text-slate-500 text-sm">/month</span>}
                    </div>
                    <Button
                      className={cn("w-full mb-6", tier.popular ? "bg-black text-white hover:bg-slate-800" : "bg-white border border-slate-200 text-slate-900 hover:bg-slate-50")}
                      onClick={goToPlansPage}
                    >
                      {tier.cta}
                    </Button>
                    <div className="space-y-3 flex-1">
                      <div className="flex items-center gap-2 text-xs text-slate-600">
                        <div className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                        {tier.credits}
                      </div>
                      {tier.details.map((detail) => (
                        <div key={detail} className="flex items-center gap-2 text-xs text-slate-600">
                          <div className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                          {detail}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <div className="inline-flex items-center justify-center p-2 bg-rose-100/50 rounded-full mb-4">
            <div className="h-2 w-2 rounded-full bg-rose-500" />
          </div>
          <h2 className="text-3xl font-bold text-slate-900 mb-12">Have questions?</h2>

          <div className="text-left space-y-4 overflow-hidden relative z-20">
            {FAQ_ITEMS.map((item, i) => {
              const isOpen = openFaqIndex === i;
              return (
                <div
                  key={i}
                  className="border-b border-slate-100 transition-all duration-300 bg-white"
                >
                  <button
                    onClick={() => setOpenFaqIndex(isOpen ? null : i)}
                    className="flex w-full items-center justify-between p-4 hover:bg-slate-50 transition-colors cursor-pointer group select-none text-left"
                    aria-expanded={isOpen}
                  >
                    <span className={cn(
                      "font-medium transition-colors",
                      isOpen ? "text-[#21728d]" : "text-slate-700 group-hover:text-black"
                    )}>
                      {item.question}
                    </span>
                    <Plus className={cn(
                      "h-4 w-4 text-slate-400 group-hover:text-black transition-transform duration-300",
                      isOpen && "rotate-45 text-[#21728d]"
                    )} />
                  </button>

                  <div
                    className={cn(
                      "transition-all duration-500 ease-in-out overflow-hidden text-slate-600",
                      isOpen ? "max-h-[300px] opacity-100 pb-5 px-4" : "max-h-0 opacity-0"
                    )}
                  >
                    <div className="pt-1 leading-relaxed">
                      {item.answer}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 bg-white border-t border-slate-100 relative overflow-hidden">
        <GridPattern />
        <div className="mx-auto max-w-4xl px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600 mb-8 shadow-sm">
            <span className="flex h-2 w-2 rounded-full bg-[#21728d]"></span>
            Introducing V2
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">What to execute next?</h2>
          <p className="text-lg text-slate-500 mb-10">Create stunning financial flows by chatting with AI.</p>
          <Button className="rounded-full bg-black text-white hover:bg-slate-800 px-8 h-12 text-base" onClick={() => document.getElementById('download')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}>
            Download
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-12 border-t border-slate-100">
        <div className="mx-auto max-w-7xl px-6 md:px-8 flex flex-col md:flex-row justify-between gap-8 text-xs text-slate-500">
          <div className="flex gap-8">
            <Link href="/nin" className="hover:text-slate-900 cursor-pointer touch-manipulation">NIN</Link>
            <Link href="#" className="hover:text-slate-900 cursor-pointer touch-manipulation">Product</Link>
            <Link href="#" className="hover:text-slate-900 cursor-pointer touch-manipulation">Resources</Link>
            <Link href="#" className="hover:text-slate-900 cursor-pointer touch-manipulation">Company</Link>
            <Link href="/ningenie/policies/terms-of-service" className="hover:text-slate-900 cursor-pointer touch-manipulation">Terms</Link>
            <Link href="/ningenie/policies/privacy-policy" className="hover:text-slate-900 cursor-pointer touch-manipulation">Privacy</Link>
            <Link href="/ningenie/policies/payments-policy" className="hover:text-slate-900 cursor-pointer touch-manipulation">Payments Policy</Link>
            <Link href="/ningenie/policies/security-policy" className="hover:text-slate-900 cursor-pointer touch-manipulation">Security</Link>
          </div>
          <div className="flex gap-4 items-center">
            <span>© 2026 All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default memo(LandingPageContentComponent)
